import React, { useState, useCallback } from 'react';
import styled from 'styled-components';
import { Container } from '../styles/GlobalStyles';
import { X, ChevronLeft, ChevronRight, Camera, Award } from 'lucide-react';

const GallerySection = styled.section`
  padding: 5rem 0;
  background: linear-gradient(
    135deg,
    #0f0f23 0%,
    #1a1a2e 50%,
    #16213e 100%
  );
  position: relative;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: 
      radial-gradient(circle at 20% 80%, rgba(139, 0, 0, 0.08) 0%, transparent 50%),
      radial-gradient(circle at 80% 20%, rgba(255, 215, 0, 0.08) 0%, transparent 50%);
    pointer-events: none;
  }
`;

const SectionHeader = styled.div`
  text-align: center;
  margin-bottom: 4rem;
  position: relative;
  z-index: 2;
`;

const SectionTitle = styled.h2`
  font-size: clamp(2rem, 5vw, 3.5rem);
  font-weight: 800;
  margin-bottom: 1rem;
  background: linear-gradient(135deg, #FFD700, #8B0000, #FFD700);
  background-size: 200% 100%;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  animation: shimmer 3s ease-in-out infinite;
  
  @keyframes shimmer {
    0%, 100% { background-position: 0% center; }
    50% { background-position: 100% center; }
  }
`;

const SectionSubtitle = styled.p`
  font-size: 1.125rem;
  color: rgba(255, 255, 255, 0.8);
  max-width: 600px;
  margin: 0 auto;
  line-height: 1.6;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  
  @media (max-width: 768px) {
    font-size: 1rem;
    padding: 0 1rem;
    flex-direction: column;
    gap: 0.25rem;
  }
`;

const CameraIcon = styled.div`
  background: linear-gradient(135deg, #8B0000, #FFD700);
  color: white;
  width: 32px;
  height: 32px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

const GalleryGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 2rem;
  margin-bottom: 3rem;
  position: relative;
  z-index: 2;
  
  @media (max-width: 768px) {
    grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
    gap: 1.5rem;
  }
`;

const GalleryCard = styled.div`
  position: relative;
  border-radius: 20px;
  overflow: hidden;
  cursor: pointer;
  transition: all 0.3s ease;
  background: rgba(255, 255, 255, 0.05);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 215, 0, 0.2);
  
  &:hover {
    transform: translateY(-8px);
    box-shadow: 0 20px 40px rgba(0, 0, 0, 0.4);
    border-color: rgba(255, 215, 0, 0.4);
  }
`;

const GalleryImage = styled.img`
  width: 100%;
  height: 350px;
  object-fit: cover;
  transition: transform 0.3s ease;
  
  ${GalleryCard}:hover & {
    transform: scale(1.05);
  }
`;

const ImageOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(
    to bottom,
    transparent 0%,
    transparent 60%,
    rgba(0, 0, 0, 0.8) 100%
  );
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  padding: 1.5rem;
  opacity: 0;
  transition: opacity 0.3s ease;
  
  ${GalleryCard}:hover & {
    opacity: 1;
  }
`;

const ImageTitle = styled.h3`
  color: white;
  font-size: 1.25rem;
  font-weight: 700;
  margin-bottom: 0.5rem;
  background: linear-gradient(135deg, #FFD700, #FFC300);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
`;

const ImageDescription = styled.p`
  color: rgba(255, 255, 255, 0.9);
  font-size: 0.9rem;
  line-height: 1.4;
  margin: 0;
`;

const QualityBadge = styled.div`
  position: absolute;
  top: 1rem;
  right: 1rem;
  background: linear-gradient(135deg, #8B0000, #FFD700);
  color: white;
  padding: 0.5rem 1rem;
  border-radius: 20px;
  font-size: 0.75rem;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 0.25rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.3);
`;



// Modal styles for fullscreen viewing
const ModalOverlay = styled.div<{ isOpen: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.95);
  backdrop-filter: blur(10px);
  z-index: 9999;
  display: ${props => props.isOpen ? 'flex' : 'none'};
  align-items: center;
  justify-content: center;
  padding: 2rem;
`;

const ModalImage = styled.img`
  max-width: 90%;
  max-height: 90%;
  object-fit: contain;
  border-radius: 15px;
  box-shadow: 0 25px 50px rgba(0, 0, 0, 0.5);
`;

const CloseButton = styled.button`
  position: absolute;
  top: 2rem;
  right: 2rem;
  background: linear-gradient(135deg, #8B0000, #FFD700);
  border: none;
  border-radius: 50%;
  width: 50px;
  height: 50px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    transform: scale(1.1);
    box-shadow: 0 10px 20px rgba(0, 0, 0, 0.3);
  }
`;

const NavigationButton = styled.button<{ direction: 'left' | 'right' }>`
  position: absolute;
  top: 50%;
  ${props => props.direction}: 2rem;
  transform: translateY(-50%);
  background: linear-gradient(135deg, #8B0000, #FFD700);
  border: none;
  border-radius: 50%;
  width: 50px;
  height: 50px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-50%) scale(1.1);
    box-shadow: 0 10px 20px rgba(0, 0, 0, 0.3);
  }
`;

const ProfessionalGallery: React.FC = () => {
  const [selectedImage, setSelectedImage] = useState<number | null>(null);

  const galleryImages = [
    {
      src: "/images/image-branding/brand-b1.jpg",
      title: "Signature Royal Burger",
      description: "Our crown jewel - perfectly crafted with premium ingredients",
      category: "Signature Burgers"
    },
    {
      src: "/images/image-branding/brand-b2.jpg", 
      title: "Classic Perfection",
      description: "Time-tested recipe that made us Detroit's #1 destination",
      category: "Classic Burgers"
    },
    {
      src: "/images/image-branding/brand-b3.jpg",
      title: "Gourmet Creation",
      description: "Elevated burger experience with artisan touches",
      category: "Gourmet Burgers"
    },
    {
      src: "/images/image-branding/brand-b4.jpg",
      title: "Fresh & Flavorful",
      description: "Every bite bursting with farm-fresh ingredients",
      category: "Fresh Ingredients"
    },
    {
      src: "/images/image-branding/brand-b5.jpg",
      title: "Premium Patty Perfection",
      description: "Hand-crafted patties grilled to absolute perfection",
      category: "Premium Quality"
    },
    {
      src: "/images/image-branding/DSC00385.jpg",
      title: "Premium Dining Atmosphere",
      description: "Elevated restaurant experience in Detroit's finest location",
      category: "Restaurant Experience"
    },
    {
      src: "/images/image-branding/DSC04583.jpg",
      title: "Fresh Ingredients Showcase",
      description: "Farm-to-table quality ingredients in every preparation",
      category: "Quality Ingredients"
    },
    {
      src: "/images/image-branding/DSC05486.jpg",
      title: "Artisan Presentation",
      description: "Each dish crafted and plated with culinary artistry",
      category: "Culinary Art"
    },
    {
      src: "/images/image-branding/dip-burger.jpg",
      title: "Au Jus Dip Burger",
      description: "Our famous dip burger - a Detroit original experience",
      category: "Specialty Item"
    },
    {
      src: "/images/image-branding/DSC05415.jpg",
      title: "Restaurant Atmosphere",
      description: "Premium dining experience in Detroit's finest burger destination",
      category: "Dining Experience"
    },
    {
      src: "/images/image-branding/DSC05456.jpg", 
      title: "Royal Service",
      description: "Elevated service that makes every visit special",
      category: "Customer Service"
    }
  ];

  const openModal = useCallback((index: number) => {
    setSelectedImage(index);
  }, []);

  const closeModal = useCallback(() => {
    setSelectedImage(null);
  }, []);

  const navigateImage = useCallback((direction: 'prev' | 'next') => {
    if (selectedImage === null) return;
    
    if (direction === 'prev') {
      setSelectedImage(selectedImage > 0 ? selectedImage - 1 : galleryImages.length - 1);
    } else {
      setSelectedImage(selectedImage < galleryImages.length - 1 ? selectedImage + 1 : 0);
    }
  }, [selectedImage, galleryImages.length]);

  return (
    <GallerySection id="gallery">
      <Container>
        <SectionHeader>
          <SectionTitle>👑 Royal Gallery</SectionTitle>
          <SectionSubtitle>
            <CameraIcon>
              <Camera size={16} />
            </CameraIcon>
            Experience Detroit's most award-winning burgers and premium dining atmosphere
          </SectionSubtitle>
        </SectionHeader>

        <GalleryGrid>
          {galleryImages.map((image, index) => (
            <GalleryCard key={index} onClick={() => openModal(index)}>
              <GalleryImage 
                src={image.src} 
                alt={image.title}
                loading="lazy"
              />
              <QualityBadge>
                <Award size={12} />
                Premium
              </QualityBadge>
              <ImageOverlay>
                <ImageTitle>{image.title}</ImageTitle>
                <ImageDescription>{image.description}</ImageDescription>
              </ImageOverlay>
            </GalleryCard>
          ))}
        </GalleryGrid>
      </Container>

      {/* Fullscreen Modal */}
      <ModalOverlay isOpen={selectedImage !== null} onClick={closeModal}>
        {selectedImage !== null && (
          <>
            <ModalImage 
              src={galleryImages[selectedImage].src}
              alt={galleryImages[selectedImage].title}
              onClick={(e) => e.stopPropagation()}
            />
            <CloseButton onClick={closeModal}>
              <X size={24} />
            </CloseButton>
            <NavigationButton 
              direction="left" 
              onClick={(e) => { e.stopPropagation(); navigateImage('prev'); }}
            >
              <ChevronLeft size={24} />
            </NavigationButton>
            <NavigationButton 
              direction="right" 
              onClick={(e) => { e.stopPropagation(); navigateImage('next'); }}
            >
              <ChevronRight size={24} />
            </NavigationButton>
          </>
        )}
      </ModalOverlay>
    </GallerySection>
  );
};

export default ProfessionalGallery; 